import { Resources } from 'constants/resources';

import { useMemo } from 'react';

import { hydraSchemaAnalyzer } from '@api-platform/admin';
import { useNavigateToRoot } from 'hooks/useNavigateToRoot';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import qs from 'qs';
import { HttpError, useDataProvider, useNotify } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { CancelButton, SaveButtonValidated } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { PageLoading } from 'shared/react-admin/Loading';
import { CreatePage } from 'shared/react-admin/Pages/CreatePage/CreatePage';

import { GatewayRulesForm } from './components/GatewayRulesForm';
import { GatewayRulesFormProvider } from './components/GatewayRulesForm/context/GatewayRulesFormContext';
import { GatewayRulesFormValues } from './components/GatewayRulesForm/hooks/useGatewayRulesForm';
import { useInitialCreateExpression } from './components/GatewayRulesForm/hooks/useInitialCreateExpressions';
import { getInitialMapping } from './components/GatewayRulesForm/utils';
import { useExpressionParams } from './hooks';
import { prepareGatewayRuleFormData } from './utils/prepareGatewayRuleFormData';

export function GatewayRulesCreate(): JSX.Element {
  const translate = useTypedTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { toRoot } = useNavigateToRoot();

  const { initialCreateExpressions, isPending } = useInitialCreateExpression();
  const { expressionParams, loadingExpressionParams } = useExpressionParams();
  const location = useLocation();

  const queryString = location.search.split('?')[1];
  const params = qs.parse(queryString);
  const defaultUrlValues = JSON.parse((params.filter || '{}') as string);

  const defaultValues = useMemo<GatewayRulesFormValues>(() => {
    return {
      active: true,
      ...defaultUrlValues,
      conditionGroup: {
        isAnd: true,
        expressions: initialCreateExpressions,
      },
      mappings: [getInitialMapping()],
    } as GatewayRulesFormValues;
  }, [initialCreateExpressions, defaultUrlValues]);

  async function onSubmit(data: GatewayRulesFormValues) {
    const modifiedData = prepareGatewayRuleFormData(data);

    try {
      await dataProvider.create(Resources.GatewayRules, {
        data: { conditionGroup: null, ...modifiedData },
      });
      notify(translate('app.gatewayRules.ruleCreated'), { type: 'info' });
      toRoot();
    } catch (error) {
      const violations = hydraSchemaAnalyzer().getSubmissionErrors(error as HttpError);
      notify(
        violations
          ? `${translate('app.common.error')}: ${Object.entries(violations)
              .map((violation) => {
                if (violation[0] === 'name') {
                  return `${violation[0]}: ${violation[1]}`;
                }
                return violation[1];
              })
              .join('\n')}`
          : `${translate('app.common.error')}:  ${(error as Error).message}`,
        {
          type: 'error',
          multiLine: true,
        },
      );
    }
  }

  if (loadingExpressionParams || isPending) {
    return <PageLoading />;
  }

  return (
    <CreatePage
      listName={translate('app.gatewayRules.titleText')}
      title={translate('app.gatewayRules.createRule')}
    >
      <GatewayRulesFormProvider
        value={{
          loadingExpressionParams,
          expressionParams,
        }}
      >
        <GatewayRulesForm defaultValues={defaultValues} onSubmit={onSubmit}>
          <CardActionsButtons
            rightActionsSlot={[
              <CancelButton key="cancel-button" linkType="back" variant="outlined" />,
              <SaveButtonValidated
                key="create-button"
                label={translate('app.gatewayRules.createRuleBtn')}
              />,
            ]}
            sx={{ padding: '24px 0' }}
          />
        </GatewayRulesForm>
      </GatewayRulesFormProvider>
    </CreatePage>
  );
}
